import './styles.scss';

import { faArrowsFromLine } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mui/material';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { AnimatedTransition } from '../../../../../components/animated-transition';
import { BackgroundMessage } from '../../../../../components/background-message';
import { ReportFilterButtons } from '../../../../../components/report-filter-buttons';
import { PORTFOLIO_TABS_NAME } from '../../../../../constants/reports/reports-data';
import { RISK_TOKEN_LIST } from '../../../../../constants/reports/reports-filters';
import { PORTFOLIO_MESSAGES } from '../../../../../constants/reports/reports-messages';
import { GlobalContext } from '../../../../../providers/global-provider';
import { PNLContext } from '../../../../../providers/pnl-provider';
import {
  DATA_DISPLAY_KEYS,
  ExchangeType,
  PortfolioManagementContext,
} from '../../../../../providers/portfolio-management';
import { IAccountOption } from '../../../../../providers/venue-provider';
import Utils from '../../../../../utils/immersive_utils';
import { useExpandCollapseRows } from '../../../hooks/pnl/use-expand-collapse-rows';
import { CalendarDualInteractivePnl } from '../calendar-dual-interactive-pnl';

import { PnlTable } from './pnl-table';
import { PnlDownloadButton } from '../pnl-download-button';

interface PnlBodyProps {
  accountOption: IAccountOption;
}

interface IDataAccount {
  data: any;
  exchange: ExchangeType;
  lastUpdate: string;
  error: any;
  bookErrors?: any;
}

export const LABELS_PNL_TRADES: any = {
  inventory: 'Inventory',
  new_trades: 'NEW TRADES PNL',
  options: 'Options',
  futures: 'Futures',
  token: 'Token',
  spot: 'Spot',
};

export const PnlBody = ({ accountOption }: PnlBodyProps) => {
  const { formatNumber } = Utils;

  const [errorPNL, setErrorPNL] = useState(false);

  const { portfolioCurrentTab } = useContext(GlobalContext);
  const {
    sortPnlTable,
    pnlData: data,
    setPnlData: setData,
    getDataPnl,
    isLoadingPnl,
    filters: { setPnlRiskToken, pnlRiskToken, time },
  } = useContext(PNLContext);
  const {
    getAggregatedData,
    getDataFromAccount,
    selectedAccountOption,
    selectedAccountsAggregated,
    selectedDataDisplayOption,
    clearAccountData,
    clearAggregatedData,
  } = useContext(PortfolioManagementContext);
  const { collapseAll, expandAll } = useExpandCollapseRows();

  const isDataDisplayPerAccount = selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount;
  const isDataPnlTreeEmpty =
    !data ||
    !data.treeData ||
    !data.treeData.explain ||
    data.treeData.explain.length === 0 ||
    portfolioCurrentTab !== PORTFOLIO_TABS_NAME.pnl;

  const bookDateFormat = (dateStr: string) => dateStr.split(' ')[0];

  const formatValue = (num: number, key = ''): JSX.Element => {
    if (num === null || num === undefined) {
      return <div key={Math.random()}>&nbsp;</div>;
    }

    const isNegative = num < 0;
    const absVal = Math.abs(num);
    const className = `total-value${isNegative ? ' negative' : ''}`;
    let decimalPlaces = 2;

    if (key !== '' && ['unit_pv_0', 'unit_pv_1'].includes(key)) {
      decimalPlaces = 4;
    }

    const formatted = formatNumber(absVal, decimalPlaces);

    return (
      <div key={Math.random()} className={className}>
        {isNegative ? `(${formatted})` : formatted}
      </div>
    );
  };

  const totalItemsTrades = (children: any[]) => {
    const totalItems = children.reduce(
      (acc: number, child: any) => acc + (child.totalItems || 0),
      0,
    );

    return totalItems;
  };

  const sumValuesOfNodes = (nodes: any[]): (number | null)[] => {
    if (!nodes || nodes.length === 0) return [];

    const length = nodes[0].values.length;
    const sums: (number | null)[] = [];

    for (let i = 0; i < length; i++) {
      let sum = 0;
      let hasNull = false;

      nodes.forEach((node) => {
        const raw = node.values[i];

        if (raw == null) {
          hasNull = true;
        } else {
          const num = parseFloat(raw.toString().replace(/,/g, ''));
          sum += isNaN(num) ? 0 : num;
        }
      });

      sums.push(hasNull ? null : sum);
    }

    return sums;
  };

  const processValues = (obj: any, displayMapping: any) => {
    if (!obj || typeof obj !== 'object') {
      return Object.values(displayMapping).map((el, i) => <div key={i}>&nbsp;</div>);
    }

    return Object.values(displayMapping).map((h: any) => {
      return obj[h] === undefined ? <div>&nbsp;</div> : obj[h];
    });
  };

  const dynamicColumns = useMemo(() => {
    if (!data || !data.treeData?.explain?.length) return [];

    const firstExplain = data.treeData.explain[0];
    const bookKeys = Object.keys(firstExplain).filter(
      (key) => !['dt_0', 'dt_1', 'total'].includes(key),
    );
    const firstBookKey = bookKeys[0];
    if (!firstBookKey) return [];

    const firstBookData = firstExplain[firstBookKey];
    if (!firstBookData?.display_mapping) return [];

    const { display_mapping } = firstBookData;
    const dynamicOrder = Object.keys(display_mapping);

    return dynamicOrder.flatMap((displayKey) => display_mapping[displayKey] || []);
  }, [data]);

  const filterPivotTree = (node: any): any | null => {
    if (
      (node.id.startsWith('date-') ||
        node.id.startsWith('book-') ||
        node.id.startsWith('token-')) &&
      node.totalItems === 0
    ) {
      return null;
    }

    if (node.children && node.children.length > 0) {
      const filteredChildren = node.children
        .map(filterPivotTree)
        .filter((child: any) => child !== null);
      node.children = filteredChildren;
    }
    return node;
  };

  const createHandleChildren = (displayMapping: any, bookDateId: string) => {
    const handleChildren: any = (prefix: any, obj: any) => {
      const objKeys = Object.keys(obj).filter((key) => key !== 'total');

      const nodes = objKeys.map((key) => {
        const childrenObj = obj[key];
        if (childrenObj.total) {
          const childrenHasPosition = !!Object.keys(childrenObj).find((childKey) => {
            return childKey !== 'total' && !!childrenObj[childKey]?.position;
          });

          const children = handleChildren(`${prefix}-${key}`, childrenObj);
          const totalItems =
            childrenHasPosition && key !== 'inventory'
              ? children.length
              : totalItemsTrades(children);

          return {
            id: `${prefix}-${key}-${bookDateId}`,
            label: LABELS_PNL_TRADES[key] || key,
            values: processValues(childrenObj['total'] || childrenObj, displayMapping),
            position: childrenHasPosition ? 'position' : false,
            totalItems,
            children,
          };
        }

        return {
          id: `${prefix}-${key}-${bookDateId}`,
          label: LABELS_PNL_TRADES[key] || key,
          position: childrenObj?.position,
          values: processValues(childrenObj['total'] || childrenObj, displayMapping),
        };
      });

      const filteredNodes = nodes.filter((node) => {
        if (node.position && node.label !== 'Inventory') return true;
        if (node.totalItems && node.totalItems > 0) return true;
        return false;
      });

      return filteredNodes;
    };

    return handleChildren;
  };

  const flattenData = (apiData: any) => {
    const flatRecords: any[] = [];
    const explainArray: any[] = apiData.explain || [];

    explainArray.forEach((dateItem) => {
      const dateLabel = bookDateFormat(dateItem.dt_1);
      const dateTotalValues = dynamicColumns.map((propKey) => {
        const rawVal = dateItem.total?.[propKey] ?? null;
        return formatValue(rawVal);
      });

      const dateNode = {
        id: `date-${dateLabel}`,
        label: dateLabel,
        values: dateTotalValues,
      };

      const bookKeys = Object.keys(dateItem).filter(
        (key) => !['dt_0', 'dt_1', 'total'].includes(key),
      );

      bookKeys.forEach((bookKey) => {
        const bookData = dateItem[bookKey];
        const bookTotalValues = dynamicColumns.map((propKey) => {
          const rawVal = bookData.total?.[propKey] ?? null;
          return formatValue(rawVal);
        });

        const bookNode = {
          id: `book-${bookKey}`,
          label: bookKey,
          values: bookTotalValues,
        };

        if (bookData.explain) {
          const tokenKeys = Object.keys(bookData.explain);
          tokenKeys.forEach((tokenKey) => {
            const tokenData = bookData.explain[tokenKey];
            const tokenTotalValues = dynamicColumns.map((propKey) => {
              const rawVal = tokenData.total?.[propKey] ?? null;
              return formatValue(rawVal);
            });

            let tokenChildren = [];
            if (bookData.display_mapping && bookData.t_1) {
              const displayMappingFiltered = Object.fromEntries(
                Object.entries(bookData.display_mapping),
              );
              const handleChildren = createHandleChildren(displayMappingFiltered, bookData.t_1);
              tokenChildren = handleChildren(tokenKey, tokenData);
            }

            const tokenNode = {
              id: `token-${tokenKey}`,
              label: tokenKey,
              values: tokenTotalValues,
              children: tokenChildren,
            };

            flatRecords.push({
              dates: dateNode,
              books: bookNode,
              tokens: tokenNode,
              instruments: tokenChildren,
            });
          });
        }
      });
    });

    return flatRecords;
  };

  const groupByDimension = (records: any[], dimension: string) => {
    const groups: any = {};
    records.forEach((record) => {
      const key = record[dimension]?.id;
      if (!groups[key]) {
        groups[key] = { group: record[dimension], records: [] };
      }
      groups[key].records.push(record);
    });
    return Object.values(groups);
  };

  const buildPivotTree = (flatRecords: any[], pivotOrder: any[]) => {
    const recursiveGroup = (records: any[], dimensions: any[]): any => {
      if (dimensions.length === 0) return [];
      const currentDimension = dimensions[0];
      const groups: any[] = groupByDimension(records, currentDimension);
      if (dimensions.length === 1) {
        return groups.map(({ records: groupRecords }) => {
          const representative = groupRecords[0];
          const leafData = representative[pivotOrder[pivotOrder.length - 1]];
          const leaf = { ...leafData };

          if (
            pivotOrder[pivotOrder.length - 1] !== 'tokens' &&
            representative.instruments &&
            representative.instruments.length > 0
          ) {
            leaf.children = representative.instruments;
          }

          if (leaf.children && leaf.children.length > 0) {
            leaf.values = sumValuesOfNodes(leaf.children);
            leaf.totalItems = totalItemsTrades(leaf.children);
          } else {
            leaf.totalItems = 0;
          }
          return leaf;
        });
      } else {
        return groups.map(({ group, records: groupRecords }) => {
          const children = recursiveGroup(groupRecords, dimensions.slice(1));
          const newValues =
            children && children.length > 0 ? sumValuesOfNodes(children) : group.values;

          return {
            ...group,
            children,
            values: newValues,
            totalItems: totalItemsTrades(children),
          };
        });
      }
    };
    return recursiveGroup(flatRecords, pivotOrder);
  };

  const appendIndexToBranch = (node: any, indexSuffix: number): void => {
    node.id = `${node.id}-${indexSuffix}`;

    if (node.children && node.children.length > 0) {
      node.children.forEach((child: any) => {
        appendIndexToBranch(child, indexSuffix);
      });
    }
  };

  const pivotTree = useMemo(
    () =>
      (sortTable: string[] = []) => {
        if (isDataPnlTreeEmpty || sortTable.length === 0) return [];

        const { treeData } = data;
        const { total } = treeData;
        const flatRecords = flattenData(treeData);
        const tree = buildPivotTree(flatRecords, sortTable);

        const filteredTree = tree.map(filterPivotTree).filter((node: any) => node !== null);

        const totalValues = dynamicColumns.map((propKey) => {
          let rawVal = total?.[propKey] ?? null;
          if (propKey === 'pv_0' || propKey === 'pv_1') {
            rawVal = null;
          }

          return formatValue(rawVal, propKey);
        });

        const totalRowTree = {
          id: 'total',
          label: 'Total',
          values: totalValues,
          totalItems: totalItemsTrades(tree),
          level: 1,
          children: filteredTree,
        };

        totalRowTree.children.forEach((child: any, index: number) => {
          appendIndexToBranch(child, index);
        });

        return [totalRowTree];
      },
    [data, sortPnlTable],
  );

  const groupedData = useMemo(() => {
    if (isDataPnlTreeEmpty) {
      return {
        headers: [],
        newHeader: [],
        lastUpdate: '',
      };
    }

    const { treeData, lastUpdate, bookErrors } = data;
    const { explain } = treeData;
    const firstExplainItem = explain[0];
    const bookKeys = Object.keys(firstExplainItem).filter((key) => !['dt_0', 'dt_1'].includes(key));

    let globalDisplayMapping: any;
    let globalDisplay: any;
    let headers: string[] = [];
    let newHeader: any[] = [];

    if (bookKeys.length > 0 && firstExplainItem[bookKeys[0]]?.display_mapping) {
      globalDisplayMapping = firstExplainItem[bookKeys[0]].display_mapping;
      headers = Object.keys(globalDisplayMapping);
      globalDisplay = firstExplainItem[bookKeys[0]].display;

      newHeader = Object.entries(globalDisplayMapping).map(([key, value], index) => {
        return {
          id: index,
          key: value,
          label: key,
          items: globalDisplay ? globalDisplay[key] : undefined,
          hasItems: globalDisplay !== undefined,
        };
      });
    }

    return {
      headers,
      newHeader,
      lastUpdate,
      bookErrors,
    };
  }, [data]);

  const filterExplainData = (originalData: any) => {
    if (!originalData || !originalData.explain) return originalData;

    const dataFiltered = { ...originalData };

    dataFiltered.explain = dataFiltered.explain.filter((item: any) => {
      const hasValidTotal = item.total && Object.keys(item.total).length > 0;
      const hasBookInfo = Object.keys(item).some(
        (key) => key !== 'dt_0' && key !== 'dt_1' && key !== 'total',
      );

      return hasValidTotal && hasBookInfo;
    });

    return dataFiltered;
  };

  const handleDataFetched = (
    dataAccount: IDataAccount | null,
    onNoData: () => void,
    onError?: () => void,
    onSuccess?: (dataFiltered: any, lastUpdate: string, bookErrors: any[]) => void,
  ) => {
    if (!dataAccount) {
      onNoData();
      return;
    }

    if (dataAccount.error) {
      onError?.();
      return;
    }

    if (dataAccount.data) {
      const filteredData = filterExplainData(dataAccount.data);
      onSuccess?.(filteredData, dataAccount.lastUpdate, dataAccount.bookErrors);
      return;
    }

    onNoData();
  };

  useEffect(() => {
    if (isLoadingPnl || !isDataDisplayPerAccount || portfolioCurrentTab !== PORTFOLIO_TABS_NAME.pnl)
      return;

    const dataParent = PORTFOLIO_TABS_NAME.pnl;
    const riskToken = pnlRiskToken.toLowerCase();
    const dataAccount = getDataFromAccount(dataParent, accountOption, riskToken);

    handleDataFetched(
      dataAccount,
      // onNoData
      () => {
        setErrorPNL(false);
        setData(null);
        getDataPnl({
          accounts: accountOption,
          periodDate: [],
          riskToken,
        });
      },
      // onError
      () => {
        setErrorPNL(true);
        setData(null);
      },
      // onSuccess
      (dataFiltered, lastUpdate, bookErrors) => {
        setData({ treeData: dataFiltered, lastUpdate, bookErrors });
        setErrorPNL(false);
      },
    );
  }, [accountOption, isLoadingPnl, portfolioCurrentTab, isDataDisplayPerAccount, pnlRiskToken]);

  useEffect(() => {
    if (isLoadingPnl || isDataDisplayPerAccount || portfolioCurrentTab !== PORTFOLIO_TABS_NAME.pnl)
      return;

    const dataParent = PORTFOLIO_TABS_NAME.pnl;
    const dataAggregatedAccounts = getAggregatedData(dataParent);
    const riskToken = pnlRiskToken.toLowerCase();
    const dataAggregatedPnlOtc = dataAggregatedAccounts?.data[riskToken];

    if (!dataAggregatedPnlOtc) {
      setErrorPNL(false);
      setData(null);
      getDataPnl({
        accounts: selectedAccountsAggregated,
        periodDate: [],
        riskToken,
      });
      return;
    }

    if (dataAggregatedAccounts?.error[riskToken]) {
      setErrorPNL(true);
      setData(null);
      return;
    }

    const filteredData = filterExplainData(dataAggregatedPnlOtc);

    setData({
      treeData: filteredData,
      lastUpdate: dataAggregatedAccounts.lastUpdate,
      bookErrors: dataAggregatedAccounts.bookErrors,
    });
    setErrorPNL(false);
  }, [isLoadingPnl, portfolioCurrentTab, isDataDisplayPerAccount, pnlRiskToken]);

  const handleCalendarDualInteractiveApply = useCallback(
    (startDate: string, endDate: string) => {
      const startDateFormat = moment(startDate).format('YYYY-MM-DD');
      const endDateFormat = moment(endDate).format('YYYY-MM-DD');
      const formatPeriodDatePnl = [startDateFormat, endDateFormat];
      const riskToken = pnlRiskToken.toLowerCase();

      if (isDataDisplayPerAccount) {
        clearAccountData(accountOption, PORTFOLIO_TABS_NAME.pnl);
        getDataPnl({
          accounts: accountOption,
          periodDate: formatPeriodDatePnl,
          riskToken,
        });
        return;
      }

      clearAggregatedData(PORTFOLIO_TABS_NAME.pnl);
      getDataPnl({
        accounts: selectedAccountsAggregated,
        periodDate: formatPeriodDatePnl,
        riskToken,
      });
    },
    [selectedDataDisplayOption, selectedAccountsAggregated, pnlRiskToken, accountOption, time],
  );

  const renderFilters = () => {
    return (
      <div className="pnl-filters">
        <div className="pnl-filters-date">
          <CalendarDualInteractivePnl
            onApply={handleCalendarDualInteractiveApply}
            disabled={isLoadingPnl}
          />
        </div>
        <ReportFilterButtons
          titleFilter="Risk Token"
          dataArr={RISK_TOKEN_LIST}
          selected={pnlRiskToken}
          setSelected={setPnlRiskToken}
          disabled={isLoadingPnl}
        />
      </div>
    );
  };

  const pnlDataTable = pivotTree(sortPnlTable.map((item) => item.id));

  const renderContent = () => {
    if (isLoadingPnl) {
      return <BackgroundMessage isLoading />;
    }

    if (errorPNL) {
      return (
        <BackgroundMessage
          message={PORTFOLIO_MESSAGES.ERROR_REQUEST_PNL}
          type="error"
          button={{
            title: PORTFOLIO_MESSAGES.ERROR_REQUEST_PNL.button,
            onClick: () =>
              isDataDisplayPerAccount
                ? getDataPnl({
                    accounts: accountOption,
                    periodDate: [],
                    riskToken: pnlRiskToken.toLowerCase(),
                  })
                : () => {},
          }}
        />
      );
    }

    if (!data) {
      return <BackgroundMessage message={PORTFOLIO_MESSAGES.NO_DATA_FOUND} />;
    }

    return (
      <AnimatedTransition className="container">
        <div className="data-table">
          <PnlTable
            data={pnlDataTable}
            header={groupedData.headers}
            lastUpdatePNL={groupedData.lastUpdate}
            newHeader={groupedData.newHeader}
            bookErrors={groupedData.bookErrors}
          />
        </div>
      </AnimatedTransition>
    );
  };

  return (
    <div className="pnl-body-container">
      <div className="options">
        {renderFilters()}
        {!isLoadingPnl && (
          <div>
            <Button className={`expand-btn`} onClick={() => expandAll(pnlDataTable)}>
              <FontAwesomeIcon icon={faArrowsFromLine} />
              <div>Expand all</div>
            </Button>
            <Button className={`expand-btn`} onClick={() => collapseAll(pnlDataTable)}>
              <FontAwesomeIcon icon={faArrowsFromLine} />
              <div> Collapse all</div>
            </Button>
            <PnlDownloadButton
              IsToDisplay={isDataDisplayPerAccount}
              data={pnlDataTable}
              header={groupedData.headers}
              account={selectedAccountOption}
              pnlRiskToken={pnlRiskToken}
            />
          </div>
        )}
      </div>
      {renderContent()}
    </div>
  );
};
